import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Calendar, Users, ChevronDown, X, Plus, Minus, SlidersHorizontal, Navigation, ArrowRight, ArrowUpDown, ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import OptimizedImage from "@/components/ui/OptimizedImage";
import {
  ApartmentIcon, HouseIcon, VillaIcon, RoomIcon, HotelBuildingIcon,
  GuesthouseIcon, BedAndBreakfastIcon, CondominiumIcon, ResortIcon,
  VacationHomeIcon, YachtIcon, CountryHouseIcon, ContainerIcon,
  AllSpacesIcon
} from "@/components/icons/PropertyTypeIcons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format, addDays } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";

import { tAuto } from "@/lib/tAuto";
// Províncias mantidas
const PROVINCES = [
  "Luanda", "Bengo", "Benguela", "Bié", "Cabinda", "Cuando", "Cuando Cubango",
  "Cuanza Norte", "Cuanza Sul", "Cunene", "Huambo", "Huíla",
  "Icolo e Bengo", "Lunda Norte", "Lunda Sul", "Malanje",
  "Moxico", "Moxico Leste", "Namibe", "Uíge", "Zaire"
];

// Categorias com a cor vermelha/salmão padrão BeraStays
const BRAND_COLOR = "#E8513D";
const PROPERTY_CATEGORIES = [
  { value: "all", label: "Todos", Icon: LayoutGrid, color: BRAND_COLOR },
  { value: "Apartamento", label: "Apartamento", Icon: ApartmentIcon, color: BRAND_COLOR },
  { value: "Casa", label: "Casa", Icon: HouseIcon, color: BRAND_COLOR },
  { value: "Vivenda", label: "Vivenda", Icon: VillaIcon, color: BRAND_COLOR },
  { value: "Quarto", label: "Quarto", Icon: RoomIcon, color: BRAND_COLOR },
  { value: "Hotel", label: "Hotel", Icon: HotelBuildingIcon, color: BRAND_COLOR },
  { value: "Hospedaria", label: "Hospedaria", Icon: GuesthouseIcon, color: BRAND_COLOR },
  { value: "Casa de Hóspedes", label: "Hóspedes", Icon: BedAndBreakfastIcon, color: BRAND_COLOR },
  { value: "Condomínio", label: "Condomínio", Icon: CondominiumIcon, color: BRAND_COLOR },
  { value: "Resort", label: "Resort", Icon: ResortIcon, color: BRAND_COLOR },
  { value: "Casa de Férias", label: "Férias", Icon: VacationHomeIcon, color: BRAND_COLOR },
  { value: "Barco", label: "Barco", Icon: YachtIcon, color: BRAND_COLOR },
  { value: "Casa de Campo", label: "Campo", Icon: CountryHouseIcon, color: BRAND_COLOR },
  { value: "Contentor", label: "Contentor", Icon: ContainerIcon, color: BRAND_COLOR },
];

interface GuestCounts {
  adults: number;
  children: number;
  babies: number;
}

interface MobileSearchBarProps {
  variant?: "guest" | "host";
  className?: string;
  onCategoryChange?: (category: string) => void;
  selectedCategory?: string;
  onOpenFilters?: () => void;
}

export function MobileSearchBar({ 
  variant = "guest", 
  className, 
  onCategoryChange,
  selectedCategory = "all",
  onOpenFilters
}: MobileSearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Estados de busca
  const [address, setAddress] = useState<string>("");
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [guests, setGuests] = useState<GuestCounts>({ adults: 2, children: 0, babies: 0 });
  const [activeCategory, setActiveCategory] = useState<string>(selectedCategory);
  
  // Estados de UI
  const [activeModal, setActiveModal] = useState<"dates" | "guests" | "filters" | "address" | null>(null);
  const [tempDates, setTempDates] = useState<{ checkIn: Date | null; checkOut: Date | null }>({
    checkIn: null,
    checkOut: null,
  });
  const [tempGuests, setTempGuests] = useState<GuestCounts>({ adults: 2, children: 0, babies: 0 });
  
  // Busca em tempo real
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalGuests = guests.adults + guests.children + guests.babies;

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (address) params.set("address", address);
    if (checkInDate) params.set("checkIn", checkInDate.toISOString().split('T')[0]);
    if (checkOutDate) params.set("checkOut", checkOutDate.toISOString().split('T')[0]);
    if (totalGuests > 0) params.set("guests", totalGuests.toString());
    params.set("adults", guests.adults.toString());
    params.set("children", guests.children.toString());
    
    if (variant === "host") {
      navigate(`/dashboard?${params.toString()}`);
    } else {
      navigate(`/explorar?${params.toString()}`);
    }
  };

  const updateGuestCount = (type: keyof GuestCounts, delta: number) => {
    setTempGuests(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + delta)
    }));
  };

  const generateDates = () => {
    const dates: Date[] = [];
    const today = new Date();
    for (let i = 0; i < 60; i++) {
      dates.push(addDays(today, i));
    }
    return dates;
  };

  const isDateSelected = (date: Date) => {
    if (!tempDates.checkIn && !tempDates.checkOut) return false;
    const dateStr = date.toISOString().split('T')[0];
    const checkInStr = tempDates.checkIn?.toISOString().split('T')[0];
    const checkOutStr = tempDates.checkOut?.toISOString().split('T')[0];
    return dateStr === checkInStr || dateStr === checkOutStr;
  };

  const isDateInRange = (date: Date) => {
    if (!tempDates.checkIn || !tempDates.checkOut) return false;
    return date > tempDates.checkIn && date < tempDates.checkOut;
  };

  const handleCategoryClick = (category: string) => {
    setActiveCategory(category);
    onCategoryChange?.(category);
  };

  const fetchSearchSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, address, city, province, images, property_type, max_guests, price_per_night')
        .or(`title.ilike.%${query}%,address.ilike.%${query}%,city.ilike.%${query}%,province.ilike.%${query}%`)
        .limit(8);
      
      if (error) throw error;
      setSearchSuggestions(data || []);
    } catch (error) {
      console.error('Erro na busca:', error);
      setSearchSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleAddressChange = (value: string) => {
    setAddress(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchSuggestions(value);
    }, 300);
  };

  const selectSuggestion = (property: any) => {
    setAddress(property.address || property.title);
    setSearchSuggestions([]);
    setActiveModal(null);
    navigate(`/espaco/${property.id}`);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Dispatch event so App.tsx can hide bottom tab bar when search is focused
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('search-focus-change', { detail: { focused: isSearchFocused } }));
  }, [isSearchFocused]);

  // Track scroll position to show/hide nav arrows
  const updateScrollState = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  const scrollCategories = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 160;
    scrollRef.current.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  return (
    <div className={cn("w-full pt-0 pb-0", className)}>
      {/* Card Principal de Pesquisa Premium */}
      <div className="bg-white/95 dark:bg-[#1C1C1E] backdrop-blur-md rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.06)] dark:shadow-none border border-gray-100/60 dark:border-[#2A2A2A] px-4 py-3 relative z-20">
        
        {variant === "guest" && (
          <>
            {/* QUANDO? - Datas */}
            <button
              onClick={() => {
                setTempDates({ checkIn: checkInDate, checkOut: checkOutDate });
                setActiveModal("dates");
              }}
              className="w-full flex items-center justify-between pb-2.5 mb-2.5 border-b border-gray-100/80 dark:border-[#2A2A2A] active:opacity-70 transition-opacity"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-[18px] h-[18px] text-[#E8513D] stroke-[1.5]" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-0">QUANDO?</div>
                  <div className="text-[15px] text-gray-900 dark:text-zinc-100 font-medium leading-tight">
                    {checkInDate && checkOutDate
                      ? `${format(checkInDate, "dd MMM", { locale: getDateLocale() })} - ${format(checkOutDate, "dd MMM", { locale: getDateLocale() })}`
                      : "Adicionar datas"}
                  </div>
                </div>
              </div>
              <ChevronDown className="w-[18px] h-[18px] text-gray-400 shrink-0" />
            </button>

            {/* HÓSPEDES */}
            <button
              onClick={() => {
                setTempGuests({ ...guests });
                setActiveModal("guests");
              }}
              className="w-full flex items-center justify-between mb-3 active:opacity-70 transition-opacity"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                  <Users className="w-[18px] h-[18px] text-[#E8513D] stroke-[1.5]" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-0">{tAuto("auto.components_search_mobilesearchbar.1", "HÓSPEDES")}</div>
                  <div className="text-[15px] text-gray-900 dark:text-zinc-100 font-medium leading-tight">
                    {totalGuests === 1 ? `1 hóspede` : `${totalGuests} hóspedes`}
                  </div>
                </div>
              </div>
              <ChevronDown className="w-[18px] h-[18px] text-gray-400 shrink-0" />
            </button>
          </>
        )}

        {/* Endereço Pill */}
        <div className="relative">
          <div className="flex items-center gap-2 bg-white dark:bg-[#121212] rounded-full border border-gray-200/80 dark:border-transparent p-1 shadow-[inset_0_2px_8px_rgba(0,0,0,0.03)] dark:shadow-[inset_0_2px_10px_rgba(0,0,0,0.3)] transition-all">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 ml-1">
              <MapPin className="w-4 h-4 text-[#E8513D] stroke-[2]" />
            </div>
          <input
              type="text"
              value={address}
              onChange={(e) => handleAddressChange(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              placeholder={tAuto("auto.components_search_mobilesearchbar.2", "Endereço, bairro ou local")}
              className="flex-1 bg-transparent text-[15px] text-gray-800 dark:text-zinc-200 placeholder:text-gray-400 dark:placeholder:text-zinc-500 outline-none min-w-0 font-medium pl-1 py-0.5"
            />
            <button
              onClick={handleSearch}
              className="shrink-0 w-11 h-11 rounded-full bg-[#E8513D] hover:bg-[#D14430] text-white flex items-center justify-center shadow-md shadow-[#E8513D]/20 active:scale-95 transition-all"
            >
              <Search className="w-5 h-5 stroke-[2]" />
            </button>
          </div>
          
          {/* Sugestões de busca em tempo real */}
          <AnimatePresence>
            {(searchSuggestions.length > 0 || isSearching) && address.length >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="absolute left-0 right-0 top-[calc(100%+8px)] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 max-h-64 overflow-y-auto"
              >
                {isSearching && (
                  <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-[#E8513D]/30 border-t-[#E8513D] rounded-full animate-spin" />
                    Buscando...
                  </div>
                )}
                
                {searchSuggestions.map((property, idx) => (
                  <button
                    key={property.id}
                    onClick={() => selectSuggestion(property)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                      {property.images?.[0] ? (
                        <OptimizedImage
                          src={property.images[0]}
                          alt={property.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Navigation className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {property.title}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" />
                        {property.address || property.city}, {property.province}
                      </div>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Categorias / Filtros Pills com setas de navegação removidas */}
      <div className="relative mt-2 mb-0 z-10 w-full">
        <div 
          ref={scrollRef}
          onScroll={updateScrollState}
          className="flex flex-nowrap gap-2.5 overflow-x-auto scrollbar-hide px-1 pb-0 snap-x snap-mandatory touch-pan-x"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {/* Botão Filtros */}
          <button
            onClick={() => {
              if (onOpenFilters) {
                onOpenFilters();
              }
            }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 dark:border-[#2A2A2A] bg-white dark:bg-[#1C1C1E] shadow-sm shrink-0 active:scale-95 transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <SlidersHorizontal className="w-[18px] h-[18px] text-[#E8513D] stroke-[1.5]" />
            <span className="text-[13px] font-medium text-gray-800 dark:text-zinc-200">Filtros</span>
          </button>

          {/* Categorias Premium */}
          {PROPERTY_CATEGORIES.map((category) => {
            const isActive = activeCategory === category.value;
            return (
              <button
                key={category.value}
                onClick={() => handleCategoryClick(category.value)}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-2 rounded-full border shrink-0 transition-all active:scale-95",
                  isActive 
                    ? "border-[#E8513D] bg-[#E8513D]/10 shadow-sm" 
                    : "border-gray-200 dark:border-[#2A2A2A] bg-white dark:bg-[#1C1C1E] shadow-sm hover:border-gray-300 dark:hover:border-[#3A3A3A]"
                )}
              >
                <category.Icon 
                  className="w-[18px] h-[18px] shrink-0" 
                  style={{ color: isActive ? "#E8513D" : category.color }}
                />
                <span 
                  className={cn(
                    "text-[13px] font-medium whitespace-nowrap",
                    isActive ? "text-[#E8513D]" : "text-gray-800 dark:text-zinc-200"
                  )}
                >
                  {category.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* MODAL: Seletor de Datas */}
      <Dialog open={activeModal === "dates"} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-sm p-0 gap-0 flex flex-col max-h-[90vh]">
          {/* Header */}
          <DialogHeader className="p-4 border-b flex-shrink-0 bg-white">
            <DialogTitle className="text-lg font-bold text-center">Selecionar Datas</DialogTitle>
          </DialogHeader>
          
          <div className="bg-gray-50 px-4 py-3 flex-shrink-0 border-b border-gray-100">
            <div className="flex justify-between items-center">
              <div className="text-center flex-1">
                <div className="text-xs text-gray-500 mb-0.5">Check-in</div>
                <div className="text-base font-bold text-[#E8513D]">
                  {tempDates.checkIn ? format(tempDates.checkIn, "dd MMM", { locale: getDateLocale() }) : "--"}
                </div>
              </div>
              <div className="w-px h-8 bg-gray-300 mx-3" />
              <div className="text-center flex-1">
                <div className="text-xs text-gray-500 mb-0.5">Check-out</div>
                <div className="text-base font-bold text-[#E8513D]">
                  {tempDates.checkOut ? format(tempDates.checkOut, "dd MMM", { locale: getDateLocale() }) : "--"}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-7 gap-1">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                <div key={day} className="text-center text-[10px] text-gray-400 py-1 font-medium">
                  {day}
                </div>
              ))}
              {generateDates().map((date) => (
                <button
                  key={date.toISOString()}
                  onClick={() => {
                    if (!tempDates.checkIn || (tempDates.checkIn && tempDates.checkOut)) {
                      setTempDates({ checkIn: date, checkOut: null });
                    } else if (tempDates.checkIn && !tempDates.checkOut) {
                      if (date > tempDates.checkIn) {
                        setTempDates({ ...tempDates, checkOut: date });
                      } else {
                        setTempDates({ checkIn: date, checkOut: null });
                      }
                    }
                  }}
                  className={cn(
                    "h-8 w-8 rounded-lg text-sm font-medium transition-all mx-auto flex items-center justify-center",
                    isDateSelected(date)
                      ? "bg-[#E8513D] text-white"
                      : isDateInRange(date)
                      ? "bg-[#E8513D]/10 text-[#E8513D]"
                      : "hover:bg-gray-100 text-gray-700"
                  )}
                >
                  {format(date, "d")}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 p-3 border-t border-gray-200 bg-white flex-shrink-0">
            <Button variant="outline" onClick={() => setTempDates({ checkIn: null, checkOut: null })} className="flex-1 h-11 text-sm rounded-xl">Limpar</Button>
            <Button onClick={() => { setCheckInDate(tempDates.checkIn); setCheckOutDate(tempDates.checkOut); setActiveModal(null); }} className="flex-1 h-11 bg-[#E8513D] hover:bg-[#D94F30] text-white font-bold text-sm rounded-xl">Aplicar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL: Seletor de Hóspedes */}
      <Dialog open={activeModal === "guests"} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-sm p-0 gap-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="text-lg font-bold">{tAuto("auto.components_search_mobilesearchbar.3", "Hóspedes")}</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">Adultos</div>
                <div className="text-sm text-gray-500">13+ anos</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => updateGuestCount("adults", -1)} disabled={tempGuests.adults <= 1} className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-semibold text-lg">{tempGuests.adults}</span>
                <button onClick={() => updateGuestCount("adults", 1)} className="w-10 h-10 rounded-full border-2 border-[#E8513D] text-[#E8513D] flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{tAuto("auto.components_search_mobilesearchbar.4", "Crianças")}</div>
                <div className="text-sm text-gray-500">2-12 anos</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => updateGuestCount("children", -1)} disabled={tempGuests.children <= 0} className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-semibold text-lg">{tempGuests.children}</span>
                <button onClick={() => updateGuestCount("children", 1)} className="w-10 h-10 rounded-full border-2 border-[#E8513D] text-[#E8513D] flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{tAuto("auto.components_search_mobilesearchbar.5", "Bebés")}</div>
                <div className="text-sm text-gray-500">{tAuto("auto.components_search_mobilesearchbar.6", "Menos de 2 anos")}</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => updateGuestCount("babies", -1)} disabled={tempGuests.babies <= 0} className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-semibold text-lg">{tempGuests.babies}</span>
                <button onClick={() => updateGuestCount("babies", 1)} className="w-10 h-10 rounded-full border-2 border-[#E8513D] text-[#E8513D] flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setTempGuests({ adults: 2, children: 0, babies: 0 })} className="flex-1 h-12">Limpar</Button>
              <Button onClick={() => { setGuests(tempGuests); setActiveModal(null); }} className="flex-1 h-12 bg-[#E8513D] hover:bg-[#D94F30] text-white font-bold">Aplicar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

